import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Award, Users, CheckCircle, Clock } from 'lucide-react';
import FilloutButton from '@/components/FilloutButton';

const FilloutHeroSection = () => {
  const { t } = useTranslation('fillout');

  const stats = [
    {
      icon: Clock,
      number: "5+",
      label: t('hero.stats.yearsExperience')
    },
    {
      icon: CheckCircle,
      number: "400+",
      label: t('hero.stats.formsCreated')
    },
    {
      icon: Users,
      number: "150+",
      label: t('hero.stats.satisfiedClients')
    }
  ];

  return (
    <section className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-background via-background to-primary/5 overflow-hidden pt-20">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-conic from-primary/20 via-transparent to-accent/20 rounded-full blur-2xl animate-[spin_20s_linear_infinite]"></div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6 sm:space-y-8 animate-fade-in text-center lg:text-left">
            <div className="space-y-4">
              <Badge 
                variant="secondary" 
                className="w-fit mx-auto lg:mx-0 bg-primary/10 text-primary border-primary/20 text-sm sm:text-base"
              >
                <Award className="w-4 h-4 mr-2" />
                {t('hero.certificationBadge')}
              </Badge>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                {t('hero.title')}
              </h1>
              
              <h2 className="text-lg sm:text-xl md:text-2xl text-muted-foreground font-medium">
                {t('hero.subtitle')}
              </h2>
              
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto lg:mx-0">
                {t('hero.description')}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
              <FilloutButton className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-semibold rounded-xl">
                {t('hero.cta')}
              </FilloutButton>
              
              <FilloutButton 
                variant="outline"
                className="w-full sm:w-auto px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-semibold rounded-xl border-2"
              >
                {t('hero.ctaSecondary')}
              </FilloutButton>
            </div>

            {/* Stats - Mobile Optimized */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 pt-6 sm:pt-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center p-4 sm:p-0">
                  <div className="flex justify-center mb-2">
                    <stat.icon className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                  </div>
                  <div className="text-xl sm:text-2xl font-bold text-foreground">
                    {stat.number}
                  </div>
                  <div className="text-xs sm:text-sm text-muted-foreground">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - Mobile Optimized */}
          <div className="relative animate-fade-in delay-300 mt-8 lg:mt-0 order-first lg:order-last">
            {/* Floating Elements - Hidden on mobile for performance */}
            <div className="hidden sm:block absolute -top-4 -right-4 w-16 sm:w-20 h-16 sm:h-20 bg-primary/20 rounded-full blur-xl animate-bounce delay-500"></div>
            <div className="hidden sm:block absolute -bottom-8 -left-8 w-24 sm:w-32 h-24 sm:h-32 bg-accent/20 rounded-full blur-xl animate-bounce delay-1000"></div>
            
            <div className="relative bg-gradient-to-br from-background/95 to-background/90 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 backdrop-blur-lg border border-primary/20 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:scale-[1.02]">
              <div className="space-y-4 sm:space-y-6">
                {/* Interactive Form Preview */}
                <div className="bg-gradient-to-br from-background/95 to-primary/5 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl border border-primary/10 hover:shadow-2xl transition-all duration-300">
                  <div className="space-y-3 sm:space-y-4">
                    {/* Form Header */}
                    <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-red-400 rounded-full"></div>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-yellow-400 rounded-full"></div>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full"></div>
                      <div className="ml-auto text-xs text-muted-foreground font-mono hidden sm:block">Fillout Builder</div>
                    </div>
                    
                    <div className="h-3 sm:h-4 bg-gradient-to-r from-primary to-primary/60 rounded w-2/3 animate-pulse"></div>
                    <div className="space-y-2 sm:space-y-3">
                      <div className="h-2 sm:h-3 bg-gradient-to-r from-muted to-muted/60 rounded w-full animate-pulse delay-100"></div>
                      <div className="h-2 sm:h-3 bg-gradient-to-r from-muted to-muted/60 rounded w-4/5 animate-pulse delay-200"></div>
                    </div>
                    
                    {/* Interactive Form Elements */}
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      <div className="group h-8 sm:h-10 bg-gradient-to-r from-background to-muted/20 rounded border-2 border-dashed border-primary/40 hover:border-primary/80 transition-all duration-300 flex items-center justify-center cursor-pointer">
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-primary rounded-full group-hover:scale-125 transition-transform"></div>
                      </div>
                      <div className="group h-8 sm:h-10 bg-gradient-to-r from-background to-muted/20 rounded border-2 border-dashed border-accent/40 hover:border-accent/80 transition-all duration-300 flex items-center justify-center cursor-pointer">
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-accent rounded-full group-hover:scale-125 transition-transform"></div>
                      </div>
                    </div>
                    
                    {/* Submit Button */}
                    <div className="h-10 sm:h-12 bg-gradient-to-r from-primary to-primary/80 rounded-lg flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <div className="w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-full group-hover:scale-110 transition-transform"></div>
                        <div className="text-xs sm:text-sm text-white font-medium opacity-80">{t('hero.submitButton')}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Enhanced Features Grid */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                {[
                  { icon: CheckCircle, text: t('hero.features.paymentBlocks'), color: "text-green-500" },
                  { icon: CheckCircle, text: t('hero.features.logicJumps'), color: "text-blue-500" },
                  { icon: CheckCircle, text: t('hero.features.teamCollaboration'), color: "text-purple-500" },
                  { icon: CheckCircle, text: t('hero.features.whiteLabel'), color: "text-orange-500" }
                ].map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors duration-300 cursor-pointer group">
                      <feature.icon className={`w-4 h-4 ${feature.color} group-hover:scale-110 transition-transform`} />
                      <span className="group-hover:font-medium transition-all text-xs sm:text-sm">{feature.text}</span>
                    </div>
                  ))}
                </div>

                {/* Trust Indicators */}
                <div className="flex items-center justify-between pt-4 border-t border-primary/10">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-xs text-muted-foreground">{t('hero.trustIndicators.uptime')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-yellow-500" />
                    <span className="text-xs text-muted-foreground">{t('hero.trustIndicators.certified')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FilloutHeroSection;